/**
 * Button to flag a post as inappropriate. Visible to authenticated users (not author).
 * Post is hidden from public list until a moderator judges.
 */

import React, { useState } from 'react';
import { postsApi } from '../../api/posts';
import { useAuth } from '../../auth/useAuth';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Textarea } from '../common/Textarea';
import { useLanguage } from '../../i18n/useLanguage';

interface FlagPostButtonProps {
  postId: string;
  authorId: string;
  onFlagged?: () => void;
  variant?: 'text' | 'button';
  label?: string;
  className?: string;
}

export const FlagPostButton: React.FC<FlagPostButtonProps> = ({
  postId,
  authorId,
  onFlagged,
  variant = 'button',
  label,
  className,
}) => {
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthor = isAuthenticated && user?.sub === authorId;
  if (!isAuthenticated || isAuthor) {
    return null;
  }

  const handleOpen = () => {
    setShowModal(true);
    setReason('');
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await postsApi.flagPost(postId, reason.trim() || undefined);
      setShowModal(false);
      onFlagged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('moderation.failedFlagPost'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {variant === 'text' ? (
        <button
          type="button"
          className={`flag-post-link ${label ? 'flag-post-link--labeled' : ''} ${className ?? ''}`.trim()}
          onClick={handleOpen}
          aria-label={t('moderation.flagPostAria')}
          title={t('moderation.report')}
        >
          <svg
            className="flag-post-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          {label && <span>{label}</span>}
        </button>
      ) : (
        <Button variant="outline" size="small" onClick={handleOpen} className={className}>
          {t('moderation.report')}
        </Button>
      )}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setReason('');
          setError(null);
        }}
        title={t('moderation.flagInappropriateTitle')}
      >
        <div className="moderation-modal">
          <p>{t('moderation.flagPostExplain')}</p>
          <Textarea
            label={t('moderation.reasonOptional')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('moderation.flagReasonOptionalPlaceholder')}
            rows={3}
          />
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <Button variant="danger" onClick={handleSubmit} disabled={loading}>
              {loading ? t('moderation.submitting') : t('moderation.flagPostSubmit')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setReason('');
                setError(null);
              }}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
